import React from 'react'
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer'
import { signPhotoUrl } from '../utils/photoUrls'

// Create styles for the PDF
const styles = StyleSheet.create({
  page: {
    padding: 25,
    fontFamily: 'Helvetica',
    fontSize: 9,
  },
  header: {
    flexDirection: 'row',
    marginBottom: 15,
    paddingBottom: 10,
    borderBottom: '2 solid #000000',
    alignItems: 'center',
  },
  logo: {
    width: 50,
    height: 50,
    marginRight: 15,
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 3,
  },
  subtitle: {
    fontSize: 10,
    color: '#000000',
  },
  section: {
    marginTop: 10,
    marginBottom: 5,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 5,
    paddingBottom: 3,
    borderBottom: '1 solid #e5e7eb',
  },
  row: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  label: {
    width: '40%',
    fontWeight: 'bold',
    color: '#374151',
  },
  value: {
    width: '60%',
    color: '#111827',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 3,
  },
  gridItem: {
    width: '50%',
    marginBottom: 4,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    fontSize: 9,
    fontWeight: 'bold',
  },
  badgeInCompliance: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
  },
  badgeNeedsAttention: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
  },
  badgeWarning: {
    backgroundColor: '#fed7aa',
    color: '#9a3412',
  },
  badgeViolation: {
    backgroundColor: '#fecaca',
    color: '#991b1b',
  },
  badgeNewOpening: {
    backgroundColor: '#cffafe',
    color: '#0e7490',
  },
  badgeNA: {
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
  },
  comments: {
    backgroundColor: '#f9fafb',
    padding: 8,
    borderRadius: 4,
    marginTop: 3,
    fontSize: 8,
    lineHeight: 1.4,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    color: '#6b7280',
    fontSize: 8,
    borderTop: '1 solid #e5e7eb',
    paddingTop: 10,
  },
  photoPage: {
    padding: 20,
    fontFamily: 'Helvetica',
  },
  photoHeader: {
    marginBottom: 10,
    paddingBottom: 8,
    borderBottom: '1 solid #e5e7eb',
  },
  photoTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 3,
  },
  photoSubtitle: {
    fontSize: 9,
    color: '#6b7280',
    textAlign: 'center',
  },
  photoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    flex: 1,
  },
  photoCell: {
    width: '48%',
    height: 340,
    marginBottom: 10,
    padding: 5,
    border: '1 solid #e5e7eb',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  photo: {
    maxWidth: '100%',
    maxHeight: 310,
    objectFit: 'contain',
  },
  photoLabel: {
    fontSize: 8,
    color: '#6b7280',
    textAlign: 'center',
    marginTop: 3,
  },
})

const formatValue = (value) => {
  if (!value) return 'N/A'
  if (value === 'yes') return 'Yes'
  if (value === 'no') return 'No'
  if (value === 'n/a') return 'N/A'
  if (value === 'in_compliance') return 'In Compliance'
  if (value === 'needs_attention') return 'Needs Attention'
  if (value === 'not_in_compliance') return 'Not in Compliance'
  if (value === 'new_opening') return 'New Opening'
  return value
}

const getDispositionBadgeStyle = (disposition) => {
  const baseStyle = styles.badge
  switch (disposition) {
    case 'in_compliance':
      return [baseStyle, styles.badgeInCompliance]
    case 'needs_attention':
      return [baseStyle, styles.badgeNeedsAttention]
    case 'warning':
      return [baseStyle, styles.badgeWarning]
    case 'violation':
      return [baseStyle, styles.badgeViolation]
    case 'new_opening':
      return [baseStyle, styles.badgeNewOpening]
    default:
      return [baseStyle, styles.badgeNA]
  }
}

const InspectionPDF = ({ inspection, establishment, idLabel = 'MXB Number' }) => {
  const formatDate = (dateString) => {
    if (!dateString) return ''
    const [year, month, day] = dateString.split('-')
    return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Image
            src="/abc-lawenforcement-patch.png"
            style={styles.logo}
          />
          <View style={styles.headerText}>
            <Text style={styles.title}>ABC INSPECTION REPORT</Text>
            <Text style={styles.subtitle}>Wake County ABC Law Enforcement</Text>
          </View>
        </View>

        {/* Establishment Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Establishment Information</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Trade Name:</Text>
            <Text style={styles.value}>{establishment.trade_name}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>{idLabel}:</Text>
            <Text style={styles.value}>{establishment.mxb_number}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Address:</Text>
            <Text style={styles.value}>
              {establishment.address}, {establishment.city}, NC {establishment.zip}
            </Text>
          </View>
        </View>

        {/* Inspection Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Inspection Details</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Inspection Date:</Text>
            <Text style={styles.value}>{formatDate(inspection.inspection_date)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Agent:</Text>
            <Text style={styles.value}>
              {inspection.agent
                ? `${inspection.agent.first_name} ${inspection.agent.last_name}`
                : 'Unknown'}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Disposition:</Text>
            <Text style={getDispositionBadgeStyle(inspection.disposition)}>
              {formatValue(inspection.disposition)}
            </Text>
          </View>
          {inspection.person_in_charge && (
            <View style={styles.row}>
              <Text style={styles.label}>Person in Charge:</Text>
              <Text style={styles.value}>{inspection.person_in_charge}</Text>
            </View>
          )}
        </View>

        {/* NC ABC Permits */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>NC ABC Permits</Text>
          <View style={styles.grid}>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Mixed Beverage:</Text>
              <Text style={styles.value}>{formatValue(inspection.mixed_beverage)}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Beer On-Premises:</Text>
              <Text style={styles.value}>{formatValue(inspection.beer_on)}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Beer Off-Premises:</Text>
              <Text style={styles.value}>{formatValue(inspection.beer_off)}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Unfortified Wine:</Text>
              <Text style={styles.value}>{formatValue(inspection.unfortified_wine)}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Fortified Wine:</Text>
              <Text style={styles.value}>{formatValue(inspection.fortified_wine)}</Text>
            </View>
          </View>
          {inspection.other_state_permits && (
            <View style={styles.row}>
              <Text style={styles.label}>Other State Permits:</Text>
              <Text style={styles.value}>{inspection.other_state_permits}</Text>
            </View>
          )}
        </View>

        {/* Local Licenses */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Local Licenses</Text>
          <View style={styles.grid}>
            <View style={styles.gridItem}>
              <Text style={styles.label}>County License:</Text>
              <Text style={styles.value}>{formatValue(inspection.county_license)}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>City License:</Text>
              <Text style={styles.value}>{formatValue(inspection.city_license)}</Text>
            </View>
          </View>
        </View>

        {/* Records */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Records</Text>
          <View style={styles.grid}>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Alcohol Records:</Text>
              <Text style={styles.value}>{formatValue(inspection.alcohol_records)}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Financial Records:</Text>
              <Text style={styles.value}>{formatValue(inspection.financial_records)}</Text>
            </View>
          </View>
        </View>

        {/* Premises */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Premises</Text>
          <View style={styles.grid}>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Storage Area:</Text>
              <Text style={styles.value}>{formatValue(inspection.storage_area)}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Advertising:</Text>
              <Text style={styles.value}>{formatValue(inspection.advertising)}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Cleanliness:</Text>
              <Text style={styles.value}>{formatValue(inspection.cleanliness)}</Text>
            </View>
          </View>
        </View>

        {/* Comments */}
        {inspection.comments && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Comments</Text>
            <View style={styles.comments}>
              <Text>{inspection.comments}</Text>
            </View>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          <Text>
            Generated on {new Date().toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Text>
          <Text>Wake County ABC Law Enforcement - ABCAxis</Text>
        </View>
      </Page>

      {/* Photo Pages - 4 photos per page */}
      {inspection.photo_urls && inspection.photo_urls.length > 0 &&
        // Chunk photos into groups of 4
        Array.from({ length: Math.ceil(inspection.photo_urls.length / 4) }, (_, pageIndex) => {
          const startIdx = pageIndex * 4
          const pagePhotos = inspection.photo_urls.slice(startIdx, startIdx + 4)
          const totalPages = Math.ceil(inspection.photo_urls.length / 4)

          return (
            <Page key={pageIndex} size="A4" style={styles.photoPage}>
              <View style={styles.photoHeader}>
                <Text style={styles.photoTitle}>
                  Inspection Photos {totalPages > 1 ? `(Page ${pageIndex + 1} of ${totalPages})` : ''}
                </Text>
                <Text style={styles.photoSubtitle}>
                  {establishment.trade_name} - {formatDate(inspection.inspection_date)}
                </Text>
              </View>
              <View style={styles.photoGrid}>
                {pagePhotos.map((photoUrl, idx) => (
                  <View key={idx} style={styles.photoCell}>
                    <Image src={async () => (await signPhotoUrl(photoUrl)) || photoUrl} style={styles.photo} />
                    <Text style={styles.photoLabel}>
                      Photo {startIdx + idx + 1} of {inspection.photo_urls.length}
                    </Text>
                  </View>
                ))}
              </View>
              <View style={styles.footer}>
                <Text>Wake County ABC Law Enforcement - ABCAxis</Text>
              </View>
            </Page>
          )
        })
      }
    </Document>
  )
}

export default InspectionPDF
